/**
 * commentary-entity-panel-map.ts — DLITE-3849
 *
 * Maps a commentary entity type to the panel key(s) that own rows of that
 * type. Used by the "follow a chip → hyperlink to the row" flow to decide
 * which panel to focus when a chip is clicked.
 *
 * Lives in the app layer (not libs/comments) on purpose: it imports each
 * panel's key constant, and the panel grids already depend on libs/comments
 * for the follow services — keeping this map inside comments would create a
 * comments ↔ panels circular dependency. The app may depend on both, so the
 * mapping belongs here. The spec pins the literal values as a guard.
 */
import { AccountListKey } from '@helios/panels/account-list'
import { ClearwaterTransactionsKey } from '@helios/panels/clearwater-transactions'
import { ExternalAccountsPanelKey } from '@helios/panels/external-accounts'
import { ExternalTransactionsKey } from '@helios/panels/external-transactions'
import { MultiBreaksKey } from '@helios/panels/multi-breaks'
import { PositionsKey } from '@helios/panels/positions'
import { SmartSuspenseKey } from '@helios/panels/smart-suspense'
import { CommentaryEntityType } from '@helios/comments'

const ENTITY_PANEL_KEYS: Record<CommentaryEntityType, string[]> = {
  account: [AccountListKey, ExternalAccountsPanelKey],
  transaction: [ClearwaterTransactionsKey],
  externalTransaction: [ExternalTransactionsKey],
  break: [MultiBreaksKey],
  asset: [PositionsKey],
  // Smart Suspense rows are owned by the (external, React-backed) Smart
  // Suspense panel; following a suspense chip activates that panel, which
  // self-focuses the row via the `commentaryFocus` input (see
  // smart-suspense-panel.component — Helios does not drive an AG-Grid api here).
  suspense: [SmartSuspenseKey],
}

/**
 * Panel key(s) that own rows of the given entity type. Returns a fresh array
 * so callers can't mutate the shared map.
 */
export function panelKeysForEntityType(type: CommentaryEntityType): string[] {
  return [...ENTITY_PANEL_KEYS[type]]
}
